import { parseCsv } from "@/lib/safety/events";

/** Two digit hour keys, "00".."23" */
export const HOUR_KEYS = Array.from({ length: 24 }, (_, h) => String(h).padStart(2, "0"));
/** Pseudo hour key for the daily aggregate (volume total / volume-weighted mean speed) */
export const DAILY_KEY = "d";

export const LINK_ID_PROPERTY = "link_id";
export const DAY_PROPERTY = "dow";
export const SPEED_LIMIT_PROPERTY = "def_speed";

export const speedProperty = (hour) => `sh${hour}`;
export const volumeProperty = (hour) => `vh${hour}`;

const toNumber = (value) => {
  if (value === undefined || value === null || value === "") {
    return null;
  }
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
};

/**
 * Convert a links CSV row (link_id, day_of_week, def_speed, daily_volume, s00..s23, v00..v23)
 * into the feature properties used by the map expressions. Missing values are omitted so
 * expressions can treat them as "no data".
 * @param {Object} row CSV row keyed by header
 * @return {Object} feature properties (without link_id / dow)
 */
export function rowToProperties(row) {
  const properties = {};
  const defSpeed = toNumber(row.def_speed);
  if (defSpeed !== null) {
    properties[SPEED_LIMIT_PROPERTY] = defSpeed;
  }

  let weightedSpeed = 0;
  let weightedVolume = 0;
  let speedSum = 0;
  let speedCount = 0;
  for (const hour of HOUR_KEYS) {
    const speed = toNumber(row[`s${hour}`]);
    const volume = toNumber(row[`v${hour}`]);
    if (speed !== null) {
      properties[speedProperty(hour)] = speed;
      if (speed > 0) {
        speedSum += speed;
        speedCount++;
        if (volume > 0) {
          weightedSpeed += speed * volume;
          weightedVolume += volume;
        }
      }
    }
    if (volume !== null) {
      properties[volumeProperty(hour)] = volume;
    }
  }

  const dailyVolume = toNumber(row.daily_volume);
  if (dailyVolume !== null) {
    properties[volumeProperty(DAILY_KEY)] = dailyVolume;
  }
  // daily speed is the volume-weighted mean of the hourly speeds, falling back to a plain mean when no hour has volume
  if (weightedVolume > 0) {
    properties[speedProperty(DAILY_KEY)] = Math.round((weightedSpeed / weightedVolume) * 10) / 10;
  } else if (speedCount > 0) {
    properties[speedProperty(DAILY_KEY)] = Math.round((speedSum / speedCount) * 10) / 10;
  }
  return properties;
}

function describe(values) {
  const sorted = [...values].sort((a, b) => a - b);
  const at = (p) => (sorted.length ? sorted[Math.min(sorted.length - 1, Math.max(0, Math.ceil(p * sorted.length) - 1))] : 0);
  return { max: sorted.length ? sorted[sorted.length - 1] : 0, p95: at(0.95), p99: at(0.99) };
}

/**
 * Join the link speed/volume rows to the roadway geometry. One feature is emitted per
 * link per day-of-week bucket so a single layer can be filtered by `dow`; links with no
 * row for a bucket still get a bare feature so they render as "no data".
 * @param {Object} roads   GeoJSON FeatureCollection of LineStrings with a link id property
 * @param {String} csvText links CSV contents
 * @param {Object} [options]
 * @param {String} [options.roadIdProperty] road feature property holding the link id (default: link_id)
 * @param {Array<String>} [options.dayBuckets] day buckets to emit; defaults to those seen in the CSV
 * @return {{ geojson: Object, dayBuckets: Array<String>, stats: Object }}
 */
export function buildSpeedVolumeGeoJSON(roads, csvText, { roadIdProperty = LINK_ID_PROPERTY, dayBuckets } = {}) {
  const rows = parseCsv(csvText);
  const byLink = new Map();
  const seenBuckets = [];
  const speeds = [];
  const hourlyVolumes = [];
  const dailyVolumes = [];
  let unmatched = 0;

  const roadIds = new Set(roads.features.map((f) => String(f.properties[roadIdProperty])));

  for (const row of rows) {
    const linkId = String(row.link_id ?? "").trim();
    const dow = String(row.day_of_week ?? "").trim().toLowerCase();
    if (!linkId || !dow) {
      continue;
    }
    if (!roadIds.has(linkId)) {
      unmatched++;
      continue;
    }
    if (!seenBuckets.includes(dow)) {
      seenBuckets.push(dow);
    }
    const properties = rowToProperties(row);
    for (const hour of HOUR_KEYS) {
      const speed = properties[speedProperty(hour)];
      const volume = properties[volumeProperty(hour)];
      if (speed > 0) {
        speeds.push(speed);
      }
      if (volume > 0) {
        hourlyVolumes.push(volume);
      }
    }
    if (properties[volumeProperty(DAILY_KEY)] > 0) {
      dailyVolumes.push(properties[volumeProperty(DAILY_KEY)]);
    }
    if (!byLink.has(linkId)) {
      byLink.set(linkId, new Map());
    }
    byLink.get(linkId).set(dow, properties);
  }

  const buckets = dayBuckets && dayBuckets.length ? dayBuckets.map((b) => String(b).toLowerCase()) : seenBuckets;
  const features = [];
  let linksWithData = 0;
  let id = 0;
  for (const road of roads.features) {
    const linkId = String(road.properties[roadIdProperty]);
    const rowsForLink = byLink.get(linkId);
    if (rowsForLink) {
      linksWithData++;
    }
    for (const dow of buckets) {
      features.push({
        type: "Feature",
        id: id++,
        geometry: road.geometry,
        properties: {
          [LINK_ID_PROPERTY]: linkId,
          [DAY_PROPERTY]: dow,
          ...(rowsForLink && rowsForLink.get(dow) ? rowsForLink.get(dow) : {})
        }
      });
    }
  }

  return {
    geojson: { type: "FeatureCollection", features },
    dayBuckets: buckets,
    stats: {
      rows: rows.length,
      unmatched,
      links: roads.features.length,
      linksWithData,
      speed: describe(speeds),
      hourlyVolume: describe(hourlyVolumes),
      dailyVolume: describe(dailyVolumes)
    }
  };
}
